import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Activity, Cpu, Route, Gauge, RefreshCw, X, Globe, AlertTriangle,
  CheckCircle2, CircleSlash, ArrowLeftRight, DollarSign, Fingerprint,
  FlaskConical, Trophy, Trash2, Plus, Play, Timer, ShieldCheck,
} from "lucide-react";
import api, { apiError } from "../lib/api";

const STATUS_STYLE = {
  SUCCESS: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10",
  PARTIAL: "text-amber-400 border-amber-500/30 bg-amber-500/10",
  FAILED: "text-red-400 border-red-500/30 bg-red-500/10",
};

const money = (n) => (n == null ? "—" : `$${Number(n).toFixed(4)}`);
const short = (s, n = 8) => (s ? String(s).slice(0, n) : "—");

function Pill({ children, className = "" }) {
  return <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${className}`}>{children}</span>;
}

function ExecTab() {
  const [data, setData] = useState({ items: [], facets: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: "", provider: "", fallback: "", web: "", q: "" });
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    api.get("/ai-ops/executions", { params: { ...params, limit: 150 } })
      .then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const openDetail = (id) => api.get(`/ai-ops/executions/${id}`).then((r) => setDetail(r.data)).catch((e) => alert(apiError(e)));

  return (
    <div className="space-y-4" data-testid="exec-tab">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Metric label="Executions (shown)" value={data.shown ?? 0} sub={`of ${data.total ?? 0}`} icon={Activity} />
        <Metric label="Fallbacks" value={data.fallback_count ?? 0} icon={ArrowLeftRight} />
        <Metric label="Failures" value={data.failed_count ?? 0} icon={AlertTriangle} />
        <Metric label="Est. cost (shown)" value={money(data.estimated_cost_shown)} icon={DollarSign} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input data-testid="exec-search" placeholder="Search task / model / property…" value={filters.q}
          onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
          className="flex-1 min-w-[180px] rounded-lg border border-border bg-card px-3 py-2 text-sm" />
        <Select testid="exec-filter-status" value={filters.status} onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
          options={[["", "All statuses"], ["SUCCESS", "Success"], ["PARTIAL", "Partial"], ["FAILED", "Failed"]]} />
        <Select testid="exec-filter-provider" value={filters.provider} onChange={(v) => setFilters((f) => ({ ...f, provider: v }))}
          options={[["", "All providers"], ...(data.facets?.providers || []).filter(Boolean).map((p) => [p, p])]} />
        <Select testid="exec-filter-fallback" value={filters.fallback} onChange={(v) => setFilters((f) => ({ ...f, fallback: v }))}
          options={[["", "Fallback: any"], ["true", "Fallback only"]]} />
        <Select testid="exec-filter-web" value={filters.web} onChange={(v) => setFilters((f) => ({ ...f, web: v }))}
          options={[["", "Web: any"], ["true", "Web search only"]]} />
        <button data-testid="exec-refresh" onClick={load} className="rounded-lg border border-border bg-card px-3 py-2 text-sm hover:border-primary/50 inline-flex items-center gap-1.5">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              {["Time", "Task", "Provider / Model", "Status", "Web", "Tokens", "Cost", "Latency", ""].map((h) => (
                <th key={h} className="px-3 py-2 text-left font-medium whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody data-testid="exec-rows">
            {data.items.map((r) => (
              <tr key={r.id} className="border-t border-border hover:bg-muted/20 cursor-pointer"
                onClick={() => openDetail(r.id)} data-testid={`exec-row-${short(r.id)}`}>
                <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">{(r.started_at || "").slice(5, 19).replace("T", " ")}</td>
                <td className="px-3 py-2 whitespace-nowrap">{r.functional_service || r.task_type}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <span className="font-medium">{r.provider || "—"}</span>
                  <span className="text-muted-foreground"> · {r.model || "—"}</span>
                  {r.fallback_used && <Pill className="ml-2 text-amber-400 border-amber-500/30 bg-amber-500/10"><ArrowLeftRight className="h-3 w-3" />fallback</Pill>}
                </td>
                <td className="px-3 py-2"><Pill className={STATUS_STYLE[r.status] || "text-muted-foreground border-border"}>{r.status}</Pill></td>
                <td className="px-3 py-2">{r.web_search ? <Globe className="h-4 w-4 text-sky-400" /> : <CircleSlash className="h-4 w-4 text-muted-foreground/40" />}</td>
                <td className="px-3 py-2 whitespace-nowrap tabular-nums text-muted-foreground">{(r.tokens_in ?? "—")}/{(r.tokens_out ?? "—")}</td>
                <td className="px-3 py-2 whitespace-nowrap tabular-nums">{money(r.estimated_cost)}</td>
                <td className="px-3 py-2 whitespace-nowrap tabular-nums text-muted-foreground">{r.duration_ms != null ? `${r.duration_ms}ms` : "—"}</td>
                <td className="px-3 py-2 text-primary text-xs">View</td>
              </tr>
            ))}
            {!loading && data.items.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-10 text-center text-muted-foreground">No executions yet. Run an AI action (Land Recon lookup, Angela chat, lead enrichment) and refresh.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {detail && <ExecDrawer detail={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function ExecDrawer({ detail, onClose }) {
  const e = detail.execution || {};
  const pt = detail.prompt_trace;
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={onClose} data-testid="exec-drawer">
      <div className="h-full w-full max-w-2xl overflow-y-auto bg-card border-l border-border p-6 space-y-5" onClick={(ev) => ev.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-wide text-primary">Execution</div>
            <h3 className="text-lg font-semibold">{e.functional_service || e.task_type}</h3>
            <div className="text-xs text-muted-foreground font-mono mt-0.5">{e.id}</div>
          </div>
          <button onClick={onClose} data-testid="exec-drawer-close" className="rounded-lg border border-border p-1.5 hover:border-primary/50"><X className="h-4 w-4" /></button>
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <KV k="Provider" v={e.provider} /><KV k="Model" v={e.model} />
          <KV k="Status" v={e.status} /><KV k="Mode" v={e.execution_mode} />
          <KV k="Fallback used" v={e.fallback_used ? "Yes" : "No"} />
          <KV k="Web search" v={e.web_search ? `Yes (${e.web_searches || 0})` : "No"} />
          <KV k="Tokens in/out" v={`${e.tokens_in ?? "—"} / ${e.tokens_out ?? "—"}`} />
          <KV k="Est. cost" v={money(e.estimated_cost)} />
          <KV k="Latency" v={e.duration_ms != null ? `${e.duration_ms} ms` : "—"} />
          <KV k="Property" v={e.property_id ? <Link className="text-primary" to={`/ai-office/executions?property=${e.property_id}`}>{short(e.property_id, 12)}</Link> : "—"} />
        </div>

        {(e.attempts || []).length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Provider attempts (same evidence)</div>
            <div className="space-y-1">
              {e.attempts.map((a, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  {a.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <AlertTriangle className="h-4 w-4 text-red-400" />}
                  <span className="text-muted-foreground">{a.role}:</span> {a.provider} · {a.model}
                  {a.error && <span className="text-red-400 text-xs truncate"> — {a.error}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {(e.citations || []).length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Citations ({e.citations.length})</div>
            <ul className="space-y-1 text-sm">
              {e.citations.slice(0, 20).map((c, i) => (
                <li key={i}><a href={c.url} target="_blank" rel="noreferrer" className="text-primary hover:underline break-all">{c.title || c.url}</a></li>
              ))}
            </ul>
          </div>
        )}

        {(e.errors || []).length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-wide text-red-400 mb-1">Errors</div>
            {e.errors.map((er, i) => <div key={i} className="text-xs text-red-300 font-mono">{er}</div>)}
          </div>
        )}

        {pt && (
          <div className="space-y-3">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Prompt trace (sanitized)</div>
            <Trace label="System" text={pt.system_prompt} />
            <Trace label="Prompt" text={pt.prompt} />
            <Trace label="Raw response" text={pt.raw_response} />
            {pt.validation_error && <div className="text-xs text-amber-400">JSON validation: {pt.validation_error}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

function Trace({ label, text }) {
  return (
    <div>
      <div className="text-[11px] font-medium text-muted-foreground mb-1">{label}</div>
      <pre className="max-h-52 overflow-auto rounded-lg border border-border bg-muted/30 p-3 text-xs whitespace-pre-wrap break-words">{text || "—"}</pre>
    </div>
  );
}

function RoutingTab() {
  const [tasks, setTasks] = useState([]);
  const load = () => api.get("/ai-ops/routing-config").then((r) => setTasks(r.data.tasks || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const swap = (t) => {
    const primary = t.secondary, secondary = t.primary;
    if (!primary || !secondary) return;
    api.put("/ai-ops/routing-config", { task_type: t.task_type, primary, secondary }).then(load).catch((e) => alert(apiError(e)));
  };

  return (
    <div className="space-y-3" data-testid="routing-tab">
      <p className="text-sm text-muted-foreground">Which model runs each task. Primary → secondary fallback uses the same evidence. Swap to flip the two for A/B testing.</p>
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>{["Task", "Primary", "Secondary", "Web", "Tier", ""].map((h) => <th key={h} className="px-3 py-2 text-left font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>
            {tasks.map((t) => (
              <tr key={t.task_type} className="border-t border-border" data-testid={`routing-row-${t.task_type}`}>
                <td className="px-3 py-2 font-medium">{t.task_type}</td>
                <td className="px-3 py-2">{(t.primary || []).join(" · ")}</td>
                <td className="px-3 py-2 text-muted-foreground">{(t.secondary || []).join(" · ") || "—"}</td>
                <td className="px-3 py-2">{t.web ? <Globe className="h-4 w-4 text-sky-400" /> : <CircleSlash className="h-4 w-4 text-muted-foreground/40" />}</td>
                <td className="px-3 py-2 text-muted-foreground">{t.tier}</td>
                <td className="px-3 py-2">
                  <button onClick={() => swap(t)} data-testid={`routing-swap-${t.task_type}`} className="text-xs text-primary inline-flex items-center gap-1 hover:underline"><ArrowLeftRight className="h-3 w-3" />Swap</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PerfTab() {
  const [models, setModels] = useState([]);
  useEffect(() => { api.get("/ai-ops/model-performance").then((r) => setModels(r.data.models || [])).catch(() => {}); }, []);
  return (
    <div className="space-y-3" data-testid="perf-tab">
      <p className="text-sm text-muted-foreground">Measured per provider/model over the last 30 days — feed the routing decisions with data, not opinion.</p>
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>{["Provider / Model", "Calls", "Success %", "Fallbacks", "Web", "Tokens in/out", "Avg latency", "Est. cost"].map((h) => <th key={h} className="px-3 py-2 text-left font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>
            {models.map((m) => (
              <tr key={`${m.provider}/${m.model}`} className="border-t border-border" data-testid={`perf-row-${m.model}`}>
                <td className="px-3 py-2 font-medium">{m.provider} · {m.model}</td>
                <td className="px-3 py-2 tabular-nums">{m.calls}</td>
                <td className="px-3 py-2 tabular-nums">{m.success_rate}%</td>
                <td className="px-3 py-2 tabular-nums">{m.fallback}</td>
                <td className="px-3 py-2 tabular-nums">{m.web}</td>
                <td className="px-3 py-2 tabular-nums text-muted-foreground">{m.tokens_in}/{m.tokens_out}</td>
                <td className="px-3 py-2 tabular-nums">{m.avg_latency_ms}ms</td>
                <td className="px-3 py-2 tabular-nums">{money(m.cost)}</td>
              </tr>
            ))}
            {models.length === 0 && <tr><td colSpan={8} className="px-3 py-10 text-center text-muted-foreground">No model activity yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Metric({ label, value, sub, icon: Icon }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={`metric-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</span>
        {Icon && <Icon className="h-4 w-4 text-primary" />}
      </div>
      <div className="mt-1 text-2xl font-bold tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

function KV({ k, v }) {
  return (<div><div className="text-[11px] uppercase tracking-wide text-muted-foreground">{k}</div><div className="font-medium">{v ?? "—"}</div></div>);
}

function Select({ value, onChange, options, testid }) {
  return (
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
      className="rounded-lg border border-border bg-card px-3 py-2 text-sm">
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}

function PropertyTab() {
  const [pid, setPid] = useState("");
  const [trace, setTrace] = useState(null);
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    if (!pid.trim()) return;
    setLoading(true);
    Promise.resolve().finally(() => setLoading(false));
  };

  const STAT = {
    VERIFIED: "text-emerald-400", SUPPORTED: "text-sky-400", INFERRED: "text-amber-400",
    NEEDS_VERIFICATION: "text-amber-400", UNKNOWN: "text-muted-foreground", CONFLICTING: "text-red-400",
  };

  return (
    <div className="space-y-4" data-testid="property-tab">
      <div className="flex gap-2">
        <input data-testid="property-id-input" value={pid} onChange={(e) => setPid(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Paste a property_id (from a Land Recon lookup or lead)…"
          className="flex-1 rounded-lg border border-border bg-card px-3 py-2 text-sm" />
        <button data-testid="property-load" onClick={load} className="rounded-lg border border-border bg-card px-4 py-2 text-sm hover:border-primary/50">Load</button>
      </div>

      {loading && <div className="text-sm text-muted-foreground">Loading…</div>}

      {trace && (
        <div className="rounded-xl border border-border p-4" data-testid="property-trace">
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Research timeline ({(trace.events || []).length})</div>
          <ol className="space-y-2">
            {(trace.events || []).map((e) => (
              <li key={e.id} className="flex items-start gap-3 text-sm">
                <span className="font-mono text-xs text-muted-foreground whitespace-nowrap">{(e.ts || "").slice(11, 19)}</span>
                <span className={e.status === "CONFLICTING" || e.status === "FAILED" ? "text-red-400" : "text-emerald-400"}>●</span>
                <span>{e.label}{e.detail ? <span className="text-muted-foreground"> — {e.detail}</span> : null}</span>
              </li>
            ))}
            {(trace.events || []).length === 0 && <li className="text-sm text-muted-foreground">No trace events for this property.</li>}
          </ol>
        </div>
      )}

      {pkg && (
        <div className="rounded-xl border border-border p-4" data-testid="property-evidence">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Evidence package ({pkg.evidence_count || 0} items · {(pkg.missing_information || []).length} missing)</div>
            <div className="text-xs text-muted-foreground">Sources: {(pkg.sources_used || []).join(", ") || "—"}</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>{["Category", "Field", "Value", "Status", "Source"].map((h) => <th key={h} className="px-3 py-2 text-left font-medium">{h}</th>)}</tr>
              </thead>
              <tbody>
                {(pkg.evidence || []).filter((e) => e.value != null && e.value !== "").map((e) => (
                  <tr key={e.evidence_id} className="border-t border-border">
                    <td className="px-3 py-1.5 text-muted-foreground">{e.category}</td>
                    <td className="px-3 py-1.5">{e.label || e.field_name}</td>
                    <td className="px-3 py-1.5 max-w-xs truncate">{String(e.value)}</td>
                    <td className={`px-3 py-1.5 font-medium ${STAT[e.verification_status] || ""}`}>{e.verification_status}</td>
                    <td className="px-3 py-1.5 text-xs text-muted-foreground max-w-[220px] truncate">
                      {e.source_url ? <a href={e.source_url} target="_blank" rel="noreferrer" className="text-primary hover:underline">{e.source_name || "source"}</a> : (e.source_name || "—")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const ms = (n) => (n == null ? "—" : n >= 1000 ? `${(n / 1000).toFixed(1)}s` : `${n}ms`);
const num = (n) => (n == null ? "—" : n);
const MODE_META = [
  ["FAST", "Fast"],
  ["DEEP", "Deep"],
  ["DUAL_AI_VERIFICATION", "Dual-AI"],
];

function ABTab() {
  const [benchmark, setBenchmark] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [pick, setPick] = useState("");
  const [modes, setModes] = useState({ FAST: true, DEEP: true, DUAL_AI_VERIFICATION: true });
  const [swap, setSwap] = useState(true);
  const [runs, setRuns] = useState([]);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadBenchmark = useCallback(() => api.get("/ab/benchmark").then((r) => setBenchmark(r.data.benchmark || [])).catch(() => {}), []);
  const loadCandidates = useCallback(() => api.get("/ab/candidates").then((r) => setCandidates(r.data.candidates || [])).catch(() => {}), []);
  const loadRuns = useCallback(() => api.get("/ab/runs").then((r) => setRuns(r.data.runs || [])).catch(() => {}), []);

  useEffect(() => { loadBenchmark(); loadCandidates(); loadRuns(); }, [loadBenchmark, loadCandidates, loadRuns]);

  // poll the active run while it is running
  useEffect(() => {
    if (!active || active.status !== "running") return;
    const t = setInterval(() => {
      api.get(`/ab/runs/${active.id}`).then((r) => {
        setActive(r.data);
        if (r.data.status !== "running") { loadRuns(); clearInterval(t); }
      }).catch(() => {});
    }, 5000);
    return () => clearInterval(t);
  }, [active, loadRuns]);

  const addParcel = () => {
    if (!pick) return;
    const c = candidates.find((x) => `${x.resource}:${x.record_id}` === pick);
    if (!c) return;
    api.post("/ab/benchmark", { resource: c.resource, record_id: c.record_id })
      .then(() => { setPick(""); loadBenchmark(); }).catch((e) => alert(apiError(e)));
  };
  const removeParcel = (id) => api.delete(`/ab/benchmark/${id}`).then(loadBenchmark).catch(() => {});

  const startRun = () => {
    const chosen = Object.entries(modes).filter(([, v]) => v).map(([k]) => k);
    if (chosen.length === 0) return alert("Pick at least one mode.");
    setBusy(true);
    api.post("/ab/run", { modes: chosen, provider_swap: swap })
      .then((r) => api.get(`/ab/runs/${r.data.run_id}`)).then((r) => { setActive(r.data); loadRuns(); })
      .catch((e) => alert(apiError(e))).finally(() => setBusy(false));
  };

  const openRun = (id) => api.get(`/ab/runs/${id}`).then((r) => setActive(r.data)).catch((e) => alert(apiError(e)));
  const applyRec = (id) => api.post(`/ab/runs/${id}/apply`).then((r) => {
    alert(`Default analysis mode set to ${r.data.default_analysis_mode}` +
      (r.data.routing_primary_swapped_to ? `; primary model → ${r.data.routing_primary_swapped_to.join(" · ")}` : ""));
  }).catch((e) => alert(apiError(e)));

  return (
    <div className="space-y-5" data-testid="ab-tab">
      <p className="text-sm text-muted-foreground max-w-3xl">
        Run the SAME fixed parcels through Fast / Deep / Dual-AI (and OpenAI-vs-Claude on identical evidence),
        then score each on <span className="text-foreground">accuracy</span>, <span className="text-foreground">sourcing</span> and
        real <span className="text-foreground">cost</span>. An impartial judge grades every verdict against only the supplied facts —
        so the routing default is driven by measured data, not opinion.
      </p>

      {/* Benchmark set */}
      <div className="rounded-xl border border-border p-4 space-y-3" data-testid="ab-benchmark">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Benchmark parcels ({benchmark.length})</div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select data-testid="ab-candidate-select" value={pick} onChange={(e) => setPick(e.target.value)}
            className="flex-1 min-w-[220px] rounded-lg border border-border bg-card px-3 py-2 text-sm">
            <option value="">Add a parcel to the benchmark…</option>
            {candidates.filter((c) => !benchmark.some((b) => b.record_id === c.record_id)).map((c) => (
              <option key={`${c.resource}:${c.record_id}`} value={`${c.resource}:${c.record_id}`}>
                {`${c.label}${c.location ? ` — ${c.location}` : ""} [${c.resource}]`}
              </option>
            ))}
          </select>
          <button data-testid="ab-add-parcel" onClick={addParcel} disabled={!pick}
            className="rounded-lg border border-border bg-card px-3 py-2 text-sm hover:border-primary/50 inline-flex items-center gap-1.5 disabled:opacity-40">
            <Plus className="h-3.5 w-3.5" /> Add
          </button>
        </div>
        {benchmark.length === 0
          ? <div className="text-sm text-muted-foreground">No parcels pinned yet. Add a few analyzed records to form a fixed test set.</div>
          : (
            <div className="flex flex-wrap gap-2" data-testid="ab-benchmark-list">
              {benchmark.map((b) => (
                <span key={b.id} className="inline-flex items-center gap-2 rounded-full border border-border bg-muted/30 pl-3 pr-1.5 py-1 text-xs">
                  <span className="font-medium">{b.label}</span>
                  {b.location && <span className="text-muted-foreground">· {b.location}</span>}
                  <button onClick={() => removeParcel(b.id)} data-testid={`ab-remove-${short(b.id)}`}
                    className="rounded-full p-0.5 hover:bg-red-500/20 hover:text-red-400"><Trash2 className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
          )}
      </div>

      {/* Run controls */}
      <div className="rounded-xl border border-border p-4 flex flex-wrap items-center gap-4" data-testid="ab-controls">
        <div className="flex items-center gap-3">
          {MODE_META.map(([k, l]) => (
            <label key={k} className="inline-flex items-center gap-1.5 text-sm cursor-pointer" data-testid={`ab-mode-${k}`}>
              <input type="checkbox" checked={!!modes[k]} onChange={(e) => setModes((m) => ({ ...m, [k]: e.target.checked }))}
                className="accent-[var(--primary)]" /> {l}
            </label>
          ))}
        </div>
        <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer border-l border-border pl-4" data-testid="ab-swap-toggle">
          <input type="checkbox" checked={swap} onChange={(e) => setSwap(e.target.checked)} className="accent-[var(--primary)]" />
          OpenAI vs Claude swap
        </label>
        <button data-testid="ab-run-btn" onClick={startRun} disabled={busy || benchmark.length === 0}
          className="ml-auto rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:opacity-90 inline-flex items-center gap-1.5 disabled:opacity-40">
          <Play className="h-4 w-4" /> {busy ? "Starting…" : "Run A/B Test"}
        </button>
      </div>

      {/* Active run detail */}
      {active && <ABRunDetail run={active} onApply={applyRec} />}

      {/* Run history */}
      <div className="rounded-xl border border-border p-4" data-testid="ab-run-history">
        <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Past A/B runs</div>
        {runs.length === 0 ? <div className="text-sm text-muted-foreground">No runs yet.</div> : (
          <div className="space-y-1">
            {runs.map((r) => (
              <button key={r.id} onClick={() => openRun(r.id)} data-testid={`ab-run-${short(r.id)}`}
                className="w-full flex items-center gap-3 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-muted/30">
                <span className="font-mono text-xs text-muted-foreground">{(r.created_at || "").slice(5, 16).replace("T", " ")}</span>
                <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${r.status === "complete" ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" : r.status === "error" ? "text-red-400 border-red-500/30 bg-red-500/10" : "text-amber-400 border-amber-500/30 bg-amber-500/10"}`}>{r.status}</span>
                <span className="text-muted-foreground">{r.parcel_count} parcels · {(r.modes || []).length} modes{r.provider_swap ? " · swap" : ""}</span>
                {r.recommendation && <span className="ml-auto text-primary text-xs inline-flex items-center gap-1"><Trophy className="h-3 w-3" />{r.recommendation.recommended_mode_label}</span>}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ABRunDetail({ run, onApply }) {
  const board = run.scoreboard || [];
  const pboard = run.provider_board || [];
  const rec = run.recommendation;
  const running = run.status === "running";
  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 space-y-4" data-testid="ab-run-detail">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold inline-flex items-center gap-2"><FlaskConical className="h-4 w-4 text-primary" /> Run {short(run.id, 8)}</div>
        <span className="text-xs text-muted-foreground">
          {running ? `Running… ${run.progress || 0}/${run.variant_total} variants` : `${run.status} · ${run.variant_total} variants`}
        </span>
      </div>
      {running && (
        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
          <div className="h-full bg-primary transition-all" style={{ width: `${Math.round(((run.progress || 0) / (run.variant_total || 1)) * 100)}%` }} />
        </div>
      )}

      {/* Recommendation */}
      {rec && (
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3" data-testid="ab-recommendation">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-wide text-emerald-400 inline-flex items-center gap-1.5"><Trophy className="h-3.5 w-3.5" /> Recommended default</div>
              <div className="text-lg font-bold mt-0.5">{rec.recommended_mode_label} mode</div>
              <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{rec.basis}</p>
              {rec.provider_note && <p className="text-sm text-sky-300 mt-1 max-w-2xl inline-flex items-start gap-1.5"><ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" />{rec.provider_note}</p>}
            </div>
            <button data-testid="ab-apply-btn" onClick={() => onApply(run.id)}
              className="rounded-lg bg-emerald-600 text-white px-3 py-2 text-sm font-medium hover:opacity-90 whitespace-nowrap">Apply to routing</button>
          </div>
        </div>
      )}

      {/* Mode scoreboard */}
      {board.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-border" data-testid="ab-scoreboard">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>{["Mode", "Success", "Accuracy", "Sourcing", "Quality", "Cost / run", "Latency", "Unsupported"].map((h) => <th key={h} className="px-3 py-2 text-left font-medium whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody>
              {board.map((b) => {
                const best = rec && rec.recommended_mode === b.mode;
                return (
                  <tr key={b.mode} className={`border-t border-border ${best ? "bg-emerald-500/5" : ""}`} data-testid={`ab-board-${b.mode}`}>
                    <td className="px-3 py-2 font-medium">{b.mode_label}{best && <Trophy className="inline h-3.5 w-3.5 text-emerald-400 ml-1.5" />}</td>
                    <td className="px-3 py-2 tabular-nums">{b.success_rate}%</td>
                    <td className="px-3 py-2 tabular-nums font-semibold">{num(b.avg_accuracy)}</td>
                    <td className="px-3 py-2 tabular-nums">{num(b.avg_sourcing)}</td>
                    <td className="px-3 py-2 tabular-nums">{num(b.avg_quality)}</td>
                    <td className="px-3 py-2 tabular-nums">{money(b.avg_cost_per_run)}</td>
                    <td className="px-3 py-2 tabular-nums text-muted-foreground">{ms(b.avg_latency_ms)}</td>
                    <td className="px-3 py-2 tabular-nums text-muted-foreground">{b.total_unsupported}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Provider head-to-head */}
      {pboard.length > 0 && (
        <div data-testid="ab-provider-board">
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5 inline-flex items-center gap-1.5"><ArrowLeftRight className="h-3.5 w-3.5" /> Primary provider on identical evidence (Deep)</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {pboard.map((p, i) => (
              <div key={p.provider} className={`rounded-lg border p-3 ${i === 0 ? "border-primary/40 bg-primary/5" : "border-border"}`} data-testid={`ab-provider-${p.provider}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium">{p.provider} <span className="text-xs text-muted-foreground">· {p.model || "—"}</span></span>
                  {i === 0 && <Trophy className="h-4 w-4 text-primary" />}
                </div>
                <div className="mt-1.5 grid grid-cols-4 gap-2 text-xs">
                  <div><div className="text-muted-foreground">Acc</div><div className="font-semibold tabular-nums">{num(p.avg_accuracy)}</div></div>
                  <div><div className="text-muted-foreground">Qual</div><div className="font-semibold tabular-nums">{num(p.avg_quality)}</div></div>
                  <div><div className="text-muted-foreground">Cost</div><div className="tabular-nums">{money(p.avg_cost_per_run)}</div></div>
                  <div><div className="text-muted-foreground">Lat</div><div className="tabular-nums">{ms(p.avg_latency_ms)}</div></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Per-variant results */}
      {(run.results || []).length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-border" data-testid="ab-results">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>{["Parcel", "Variant", "Verdict", "Provider", "Acc", "Src", "Qual", "Cost", "Latency"].map((h) => <th key={h} className="px-3 py-2 text-left font-medium whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody>
              {(run.results || []).map((r, i) => (
                <tr key={i} className="border-t border-border">
                  <td className="px-3 py-2 max-w-[160px] truncate">{r.label || short(r.record_id)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.mode_label}{r.primary_override ? ` · ${r.primary_override}` : ""}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {r.verdict}
                    {r.needs_human && <span className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] text-amber-400"><AlertTriangle className="h-3 w-3" />verify</span>}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-muted-foreground text-xs">{r.provider}·{r.model}{r.fallback_used ? " ⤳" : ""}</td>
                  <td className="px-3 py-2 tabular-nums">{r.judged ? num(r.accuracy) : "—"}</td>
                  <td className="px-3 py-2 tabular-nums">{r.judged ? num(r.sourcing) : "—"}</td>
                  <td className="px-3 py-2 tabular-nums">{r.judged ? num(r.quality) : "—"}</td>
                  <td className="px-3 py-2 tabular-nums">{money(r.cost_usd)}</td>
                  <td className="px-3 py-2 tabular-nums text-muted-foreground">{ms(r.latency_ms)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


export default function AIExecutions() {
  const [tab, setTab] = useState("executions");
  useEffect(() => { document.title = "ARQITEK — AI Execution Log"; }, []);
  const TABS = [
    ["executions", "Execution Log", Activity],
    ["routing", "Model Routing", Route],
    ["performance", "Model Performance", Gauge],
    ["abtest", "A/B Harness", FlaskConical],
    ["property", "Property Trace", Fingerprint],
  ];
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="ai-executions-page">
      <div>
        <span className="block text-xs uppercase tracking-[0.15em] text-primary mb-2">AI Observability</span>
        <div className="flex items-center gap-3">
          <Cpu className="h-7 w-7 text-primary" />
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">AI Execution Log</h1>
        </div>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Every LLM call the platform makes — provider, model, tokens, cost, latency, fallback, web search and the full sanitized prompt/response. This is the ground truth for evaluating the agents.
        </p>
      </div>

      <div className="flex gap-1 border-b border-border">
        {TABS.map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <Icon className="h-4 w-4" />{label}
          </button>
        ))}
      </div>

      {tab === "executions" && <ExecTab />}
      {tab === "routing" && <RoutingTab />}
      {tab === "performance" && <PerfTab />}
      {tab === "abtest" && <ABTab />}
      {tab === "property" && <PropertyTab />}
    </div>
  );
}
